import { useEffect, useRef } from 'react';

import dayjs from 'dayjs';
import Plotly from 'plotly.js/dist/plotly';

// A trackpad keeps emitting wheel events from inertia after the fingers lift.
// Committing before that tail finishes would fire a fetch per momentum frame,
// so the gesture is treated as ongoing until this long a gap appears.
const SETTLE_MS = 180;

// Below this, a wheel event is treated as vertical scrolling that happens to
// have a little sideways noise in it, and is left to the page.
const HORIZONTAL_BIAS = 1.2;

interface Options {
    /** The plot's DOM node, from Plot's onInitialized. */
    graphRef: { current: any };
    /** Element the listener attaches to. */
    containerRef: { current: HTMLElement | null };
    /** Oldest instant the chart may show, or null when unknown. */
    minTimeRef: { current: any };
    /** Called once the gesture settles, with the axis Plotly ended on. */
    onSettled: (from: string, to: string) => void;
    /** Suppresses relayout-driven commits while the gesture is mid-flight. */
    onGestureChange: (active: boolean) => void;
    /** Called when a scroll was refused because it would pass the floor. */
    onBlocked: () => void;
}

/**
 * Horizontal trackpad/wheel scrolling as a second way to pan the chart.
 *
 * During the gesture the axis is moved with `Plotly.relayout` directly rather
 * than through React state. A state update per wheel tick would re-render and
 * re-diff the whole plot dozens of times a second; going straight at Plotly
 * keeps it to an axis-only redraw, which is what the native drag does too.
 * React is told once, after the wheel has settled.
 */
export function useHorizontalWheelPan({
    graphRef,
    containerRef,
    minTimeRef,
    onSettled,
    onGestureChange,
    onBlocked,
}: Options) {
    const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const active = useRef(false);
    const blockedAnnounced = useRef(false);

    // The listener is attached once and reads its collaborators through refs, so
    // a re-render can't detach and reattach it mid-gesture.
    const handlers = useRef({ onSettled, onGestureChange, onBlocked });
    handlers.current = { onSettled, onGestureChange, onBlocked };

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return undefined;

        const onWheel = (event: WheelEvent) => {
            const graph = graphRef.current;
            const axis = graph?._fullLayout?.xaxis;
            if (!axis || !axis.range) return;

            const { deltaX, deltaY } = event;
            if (Math.abs(deltaX) < Math.abs(deltaY) * HORIZONTAL_BIAS) return;
            if (deltaX === 0) return;

            // Ours now: without this the dialog scrolls sideways underneath.
            event.preventDefault();

            const pixels = axis._length;
            if (!pixels) return;

            const from = dayjs(axis.range[0]);
            const to = dayjs(axis.range[1]);
            if (!from.isValid() || !to.isValid()) return;

            const width = to.valueOf() - from.valueOf();
            if (!(width > 0)) return;

            // Scrolling right travels forward in time, matching how a horizontal
            // list scrolls; dragging is the opposite because you grab the content
            // rather than the viewport.
            let shift = (deltaX / pixels) * width;

            // Never past now. Clamping here rather than on commit keeps the chart
            // from visibly overshooting and snapping back.
            const now = dayjs().valueOf();
            if (to.valueOf() + shift > now) shift = now - to.valueOf();

            // Nor before the start of the device's history. Refusing the scroll
            // outright — rather than letting it drift and pulling it back — is
            // what makes the chart feel like it has hit an end.
            const floor = minTimeRef.current;
            if (floor) {
                const floorMs = dayjs(floor).valueOf();
                if (from.valueOf() + shift < floorMs) {
                    shift = floorMs - from.valueOf();
                    if (!blockedAnnounced.current) {
                        blockedAnnounced.current = true;
                        handlers.current.onBlocked();
                    }
                }
            }

            if (shift === 0) return;

            if (!active.current) {
                active.current = true;
                handlers.current.onGestureChange(true);
            }

            const nextFrom = dayjs(from.valueOf() + shift).format('YYYY-MM-DD HH:mm:ss');
            const nextTo = dayjs(to.valueOf() + shift).format('YYYY-MM-DD HH:mm:ss');
            Plotly.relayout(graph, { 'xaxis.range': [nextFrom, nextTo] });

            if (settleTimer.current) clearTimeout(settleTimer.current);
            settleTimer.current = setTimeout(() => {
                active.current = false;
                blockedAnnounced.current = false;
                handlers.current.onGestureChange(false);

                const settled = graphRef.current?._fullLayout?.xaxis?.range;
                if (settled) handlers.current.onSettled(String(settled[0]), String(settled[1]));
            }, SETTLE_MS);
        };

        node.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            node.removeEventListener('wheel', onWheel);
            if (settleTimer.current) clearTimeout(settleTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
